import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axiosInstance from './../axiosInstance.js';
import { localizeArt } from './../localize.js';
import MyUserContext from './../myUserContext.js';
import ArtThumbnail from './ArtThumbnail.js';
import { Paper, Text } from '@mantine/core';
import TagBadge from './TagBadge';

const LogbookHistory = ({ userId: userIdProp = null, embedded = false } = {}) => {
  const [logbookItems, setLogbookItems] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedItemKey, setSelectedItemKey] = useState(null);
  // Filter: null = show all months, otherwise the index of the
  // single month in groupedByMonth to display.
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(null);
  const scrollDetailIntoView = (el) => {
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        window.scrollBy({ top: rect.bottom - window.innerHeight + 60, behavior: 'smooth' });
      }
    });
  };
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // Prop wins over query param — lets this component be embedded
  // inside another page (e.g. the Logbook tab on /users/:id/likes)
  // without depending on the URL.
  const userId = userIdProp || queryParams.get('userId');
  const artId = queryParams.get('artId');
  const isOwnLogbook = !userId || userId === myUser?.myUserInfo?.myUserId;

  const updateItemNote = (id, note) => {
    setLogbookItems((prev) => (prev || []).map((it) => it.id === id ? { ...it, note } : it));
  };
  const saveItemNote = (id, note) => {
    axiosInstance
      .post(`/art/logbook/${id}/update`, { note: note || '' })
      .catch((err) => console.error('Failed to update note:', err));
  };

  useEffect(() => {
    let isActive = true;
    const fetchLogbookHistory = async () => {
      const baseParams = new URLSearchParams();
      if (userId) {
        baseParams.set('userId', userId);
      }
      if (artId) {
        baseParams.set('artId', artId);
      }

      const firstParams = new URLSearchParams(baseParams);
      firstParams.set('count', '10');

      try {
        const firstResponse = await axiosInstance.get(`/logbookHistory?${firstParams.toString()}`);
        if (!isActive) return;
        setLogbookItems(firstResponse.data);

        if (!artId) {
          const secondParams = new URLSearchParams(baseParams);
          secondParams.set('count', '10000');
          secondParams.set('offset', '10');
          setIsLoadingMore(true);
          const secondResponse = await axiosInstance.get(`/logbookHistory?${secondParams.toString()}`);
          setIsLoadingMore(false);
          if (!isActive) return;
          if (Array.isArray(secondResponse.data) && secondResponse.data.length > 0) {
            setLogbookItems((prev) => (prev || []).concat(secondResponse.data));
          }
        }
      } catch (error) {
        setIsLoadingMore(false);
        console.error('Failed to fetch logbook history:', error);
      }
    };

    const fetchUser = () => {
      if (userId) {
        axiosInstance
          .get(`/users/${userId}`)
          .then((response) => setUser(response.data))
          .catch((error) => console.error('Failed to fetch user:', error));
      }
    };

    fetchLogbookHistory();
    fetchUser();
    return () => { isActive = false; };
  }, [userId, artId]);

  if (logbookItems === null) {
    return <div></div>;
  }

  // Group items by month
  const groupedByMonth = [];
  let currentGroup = null;
  for (const item of logbookItems) {
    const monthKey = new Date(item.logTime).getTime() === 0
      ? (language === 'ru' ? 'Давно' : 'Long time ago')
      : new Date(item.logTime).toLocaleString(language, { month: 'long', year: 'numeric' });

    if (!currentGroup || currentGroup.month !== monthKey) {
      currentGroup = { month: monthKey, items: [] };
      groupedByMonth.push(currentGroup);
    }
    currentGroup.items.push(item);
  }

  return (
    <div className="px-3 pb-6 mt-2">

      <div className="flex flex-col items-center">
        {!embedded && (
          <h1 className="text-2xl font-bold mb-1">
            {language === 'ru' ? 'Журнал' : 'Logbook'}
          </h1>
        )}
        {!embedded && user && (
          <div className="flex items-center gap-2 mb-2">
            <Link to={`/users/${userId}/likes`}>
              <img src={user.avatarUrl} alt={user.userName} className="w-7 h-7 rounded-full" />
            </Link>
            <Text size="sm" c="dimmed">{user.userName}</Text>
          </div>
        )}
      </div>

      {groupedByMonth.length > 1 && (
        <div
          className="mb-4 flex flex-nowrap items-center gap-2 overflow-x-auto no-scrollbar"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {groupedByMonth.map((g, idx) => {
            const isSelected = selectedMonthIdx === idx;
            return (
              <TagBadge
                key={g.month}
                component="button"
                type="button"
                className="cursor-pointer flex-shrink-0"
                radius="md"
                size="md"
                variant="filled"
                color={isSelected ? 'blue' : 'gray'}
                onClick={() => setSelectedMonthIdx((prev) => (prev === idx ? null : idx))}
              >
                {g.month}
                <Text span size="xs" style={{ marginLeft: 4, opacity: 0.75 }}>
                  {g.items.length}
                </Text>
              </TagBadge>
            );
          })}
        </div>
      )}

      {logbookItems.length === 0 && (
        <div className="flex justify-center text-center mt-8">
          <Text c="dimmed">{language === 'ru' ? 'Пока ничего не добавлено в журнал' : "Nothing added to the logbook yet"}</Text>
        </div>
      )}

      {isLoadingMore && (
        <div className="flex justify-center mb-4">
          <div className="w-full max-w-md">
            <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--mantine-color-default)' }}>
              <div className="similars-progress-bar h-full w-1/3 rounded-full" style={{ backgroundColor: 'var(--mantine-color-default-border)' }}></div>
            </div>
          </div>
        </div>
      )}

      {(selectedMonthIdx !== null && groupedByMonth[selectedMonthIdx]
        ? [groupedByMonth[selectedMonthIdx]]
        : groupedByMonth
      ).map((group) => {
        const monthMovies = group.items.filter(i => i.art.category === 1).length;
        const monthBooks = group.items.filter(i => i.art.category === 3).length;
        const monthGames = group.items.filter(i => i.art.category === 2).length;
        const parts = [
          monthMovies > 0 && `${monthMovies} ${language === 'ru' ? (monthMovies === 1 ? 'фильм' : 'фильмов') : (monthMovies === 1 ? 'movie' : 'movies')}`,
          monthBooks > 0 && `${monthBooks} ${language === 'ru' ? (monthBooks === 1 ? 'книга' : 'книг') : (monthBooks === 1 ? 'book' : 'books')}`,
          monthGames > 0 && `${monthGames} ${language === 'ru' ? (monthGames === 1 ? 'игра' : 'игр') : (monthGames === 1 ? 'game' : 'games')}`,
        ].filter(Boolean);

        return (
          <div key={group.month} className="mb-6">
            <div className="flex items-baseline gap-2 mb-2">
              <Text size="lg" fw={700} c="blue">{group.month}</Text>
              {parts.length > 0 && (
                <Text size="sm" c="dimmed">{parts.join(' · ')}</Text>
              )}
            </div>
            {(() => {
              const cols = window.innerWidth >= 640 ? 9 : 6;
              const elements = [];
              let globalIdx = 0;
              for (let i = 0; i < group.items.length; i += cols) {
                const rowItems = group.items.slice(i, i + cols);
                const rowStartIdx = globalIdx;
                const rowKeys = rowItems.map((_, j) => `${group.month}-${rowStartIdx + j}`);
                const selectedInThisRow = selectedItemKey && rowKeys.includes(selectedItemKey);
                const selectedItem = selectedInThisRow
                  ? rowItems[rowKeys.indexOf(selectedItemKey)]
                  : null;

                elements.push(
                  <div key={`row-${i}`} className="grid grid-cols-6 sm:grid-cols-9 gap-1.5 mb-1.5">
                    {rowItems.map((item, j) => {
                      const itemKey = `${group.month}-${rowStartIdx + j}`;
                      const localized = localizeArt(item.art, language);
                      const dayStr = new Date(item.logTime).getTime() === 0
                        ? ''
                        : new Date(item.logTime).toLocaleDateString(language, { day: 'numeric', month: 'short' });
                      return (
                        <div
                          key={itemKey}
                          className="cursor-pointer"
                          onClick={() => setSelectedItemKey(selectedItemKey === itemKey ? null : itemKey)}
                        >
                          <div className="relative">
                            <ArtThumbnail art={item.art} displayCheckmark={false} />
                            {item.finished && (
                              <div className="absolute bottom-1 right-1 rounded-full p-0.5" style={{ backgroundColor: 'var(--mantine-color-body)' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" style={{ color: 'var(--mantine-color-green-text)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <Text size="xs" fw={500} className="mt-0.5 px-0.5 line-clamp-2 text-center leading-tight">
                            {localized.name}
                          </Text>
                          {dayStr && (
                            <Text size="xs" c="dimmed" className="text-center leading-tight">
                              {dayStr}
                            </Text>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );

                if (selectedItem) {
                  elements.push(
                    <Paper key={`detail-${i}`} ref={scrollDetailIntoView} shadow="xs" radius="md" className="mb-2 p-3" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                      <div className="flex items-center gap-3">
                        <Link to={`/arts/${selectedItem.art.id}`}>
                          <img src={selectedItem.art.thumbnailUrl} alt="" className="w-14 h-14 object-cover rounded-md" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/arts/${selectedItem.art.id}`}>
                            <Text size="sm" fw={500} className="truncate">{localizeArt(selectedItem.art, language).name}</Text>
                          </Link>
                          {new Date(selectedItem.logTime).getTime() !== 0 && (
                            <Text size="xs" c="dimmed">
                              {new Date(selectedItem.logTime).toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' })}
                            </Text>
                          )}
                          {isOwnLogbook ? (
                            <input
                              type="text"
                              className="text-xs w-full mt-1 p-1 rounded focus:outline-none"
                              style={{ backgroundColor: 'transparent', color: 'var(--mantine-color-dimmed)', border: 'none', fontStyle: 'italic' }}
                              value={selectedItem.note || ''}
                              maxLength={100}
                              placeholder={language === 'ru' ? 'заметка...' : 'note...'}
                              onChange={(e) => updateItemNote(selectedItem.id, e.target.value)}
                              onBlur={() => saveItemNote(selectedItem.id, selectedItem.note)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.target.blur();
                                }
                              }}
                            />
                          ) : (
                            selectedItem.note && (
                              <Text size="xs" c="dimmed" className="mt-1" style={{ fontStyle: 'italic' }}>
                                {selectedItem.note}
                              </Text>
                            )
                          )}
                        </div>
                        {selectedItem.artEventId && (
                          <Link to={`/artEvents/${selectedItem.artEventId}`}>
                            <img src="/chat_icon.png" className="w-5 h-5" style={{ filter: 'var(--icon-filter)' }} />
                          </Link>
                        )}
                      </div>
                    </Paper>
                  );
                }

                globalIdx += rowItems.length;
              }
              return elements;
            })()}
          </div>
        );
      })}
    </div>
  );
};

export default LogbookHistory;
